import type { TablesView } from "../../../../../api/configuration";
import type { SchemaRegion } from "../../../../../api/restaurantSchema";
import {
  useConfiguration,
  useUpdateUserConfiguration,
} from "../../../../../hooks/useConfiguration";
import { useRestaurantSchema } from "../../../../../hooks/useRestaurantSchema";
import { useViewport } from "../../../../../hooks/useViewport";

/** Има ли в схемата поне една маса (районите без маси не стигат). */
const schemaHasTables = (regions: SchemaRegion[]) =>
  regions.some((region) => region.elements.some((element) => element.kind === "table"));

/**
 * Масите като списък или върху схемата — ЛИЧНА настройка (`tablesView`, org
 * базата, следва служителя на всяко устройство), като Плочки/Таблица:
 * превключвателят в горната лента я записва директно (оптимистично, без
 * localStorage). Схемата е само на десктоп и само когато в „Схема на
 * заведението" има поне една маса — иначе екранът е Списък и превключвател
 * няма, без да се трие изборът. Схемата се чете веднъж при отваряне (същият
 * кеш като режима, без четене при фокус — всяко четене е ред в одита);
 * докато се зарежда, превключвателят го няма, а избраната „Схема" си стои
 * (тя сама показва зареждането). Неуспешно четене без нищо в кеша (напр.
 * по-стара каса в локален режим не сервира схемата) оставя старото поведение —
 * режимът казва какво не е наред; паднало опресняване на схема без маси не
 * връща превключвателя.
 */
export const useTablesView = () => {
  const { isDesktop } = useViewport();
  const { configuration } = useConfiguration();
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();
  // `?? "list"` — самоличност от кеша отпреди полето да съществува.
  const tablesView: TablesView = configuration.tablesView ?? "list";
  const schemaQuery = useRestaurantSchema({ enabled: isDesktop, refetchOnFocus: false });
  const hasTables = schemaQuery.data ? schemaHasTables(schemaQuery.data) : null;
  // null — още не се знае (зарежда се или четенето е паднало).
  const noSchemaTables = hasTables === false;

  const setTablesView = (view: TablesView) => {
    if (view !== tablesView) updateUserConfiguration({ tablesView: view });
  };

  return {
    tablesView,
    setTablesView,
    /** Режимът „Схема" е в сила (избран, десктоп и схемата има маси). */
    schemaMode: isDesktop && tablesView === "schema" && !noSchemaTables,
    /** Превключвателят се показва само там, където схемата е възможна. */
    canSwitch: isDesktop && (hasTables === true || (hasTables === null && schemaQuery.isError)),
  };
};
